import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Loader2, MapPin, Clock, RefreshCw, X, Route as RouteIcon, Navigation,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  loadGoogleMaps, formatDistance, formatDuration, timeAgo, pinIcon,
} from "../../utils/googleMaps";

const API = process.env.REACT_APP_BACKEND_URL;
const authCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_META = {
  live: { label: "Live", color: "#22c55e" },
  stale: { label: "Stale", color: "#f59e0b" },
  offline: { label: "Offline", color: "#94a3b8" },
  unavailable: { label: "Not sharing", color: "#64748b" },
};

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

// Manager/dispatcher live technician map: latest shared locations with
// Live/Stale/Offline states, filters, and per-tech day route overlay.
export default function TeamMapPage() {
  const [techs, setTechs] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const techMarkers = useRef([]);
  const routeOverlays = useRef([]);

  const fetchTechs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/team/map/technicians`, authCfg());
      setTechs(res.data.technicians || []);
    } catch (e) {
      if (techs === null) setTechs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTechs();
    const id = setInterval(fetchTechs, 30000);
    return () => clearInterval(id);
  }, [fetchTechs]);

  const visibleTechs = (techs || []).filter((t) => {
    if (filterText && !(t.name || "").toLowerCase().includes(filterText.toLowerCase())) return false;
    if (filterStatus !== "all" && t.location_status !== filterStatus) return false;
    return true;
  });
  // Derived from the freshest poll so status/updated-ago never go stale
  const selected = (techs || []).find((t) => t.technician_id === selectedId) || null;

  // Render tech markers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapRef.current || techs === null) return;
      let maps;
      try {
        maps = await loadGoogleMaps("token");
      } catch {
        setMapError("Couldn't load Google Maps. Enable the Maps JavaScript API for the company's Google key.");
        return;
      }
      if (cancelled) return;
      if (!mapObj.current) {
        mapObj.current = new maps.Map(mapRef.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: "greedy",
          clickableIcons: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
      }
      const map = mapObj.current;
      techMarkers.current.forEach((m) => m.setMap(null));
      techMarkers.current = [];
      const bounds = new maps.LatLngBounds();
      let any = false;
      visibleTechs.forEach((t) => {
        if (!t.location) return;
        const meta = STATUS_META[t.location_status] || STATUS_META.unavailable;
        const marker = new maps.Marker({
          position: { lat: t.location.lat, lng: t.location.lng },
          map,
          label: { text: initials(t.name), color: "#fff", fontSize: "11px", fontWeight: "700" },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 15,
            fillColor: meta.color,
            fillOpacity: t.location_status === "live" ? 1 : 0.75,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
          title: `${t.name} — ${meta.label}`,
        });
        marker.addListener("click", () => setSelectedId(t.technician_id));
        techMarkers.current.push(marker);
        bounds.extend({ lat: t.location.lat, lng: t.location.lng });
        any = true;
      });
      if (any && !routeData) {
        if (techMarkers.current.length === 1) {
          map.setCenter(techMarkers.current[0].getPosition());
          map.setZoom(12);
        } else {
          map.fitBounds(bounds, 64);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techs, filterText, filterStatus]);

  const showRoute = async (tech) => {
    setRouteLoading(true);
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const res = await axios.get(
        `${API}/api/team/map/tech-route/${tech.technician_id}?date=${today}`, authCfg());
      setRouteData({ tech, ...res.data });
      if (!mapObj.current) return; // map failed to load — banner/list still show
      const maps = await loadGoogleMaps("token");
      const map = mapObj.current;
      routeOverlays.current.forEach((o) => o.setMap(null));
      routeOverlays.current = [];
      const bounds = new maps.LatLngBounds();
      // Group duplicate-address stops like the tech map ("2+" label)
      const groups = new Map();
      (res.data.stops || []).forEach((s) => {
        const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(s);
      });
      groups.forEach((groupStops) => {
        const first = groupStops[0];
        const label = groupStops.length > 1 ? `${first.stop_number}+` : String(first.stop_number);
        const m = new maps.Marker({
          position: { lat: first.lat, lng: first.lng },
          map,
          label: { text: label, color: "#fff", fontSize: "12px", fontWeight: "700" },
          icon: pinIcon(maps, first.status_color || "#2563eb", { size: 40, withDot: false }),
          title: groupStops.map((s) => `${s.stop_number}. ${s.customer_name}`).join(", "),
        });
        routeOverlays.current.push(m);
        bounds.extend({ lat: first.lat, lng: first.lng });
      });
      (res.data.route?.legs || []).forEach((leg) => {
        if (!leg.polyline) return;
        const line = new maps.Polyline({
          path: maps.geometry.encoding.decodePath(leg.polyline),
          map, strokeColor: "#2563eb", strokeOpacity: 0.85, strokeWeight: 4,
        });
        routeOverlays.current.push(line);
      });
      if (tech.location) bounds.extend({ lat: tech.location.lat, lng: tech.location.lng });
      if ((res.data.stops || []).length) map.fitBounds(bounds, 64);
    } catch (_) {
      setRouteData({ tech, stops: [], unmapped: [], route: null, failed: true });
    } finally {
      setRouteLoading(false);
    }
  };

  const clearRoute = () => {
    routeOverlays.current.forEach((o) => o.setMap(null));
    routeOverlays.current = [];
    setRouteData(null);
  };

  const meta = (t) => STATUS_META[t.location_status] || STATUS_META.unavailable;

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="team-map-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-foreground">Team Map</h1>
          <p className="text-sm text-muted-foreground">Latest shared technician locations — live, stale and offline states are always labeled</p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchTechs} data-testid="team-map-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="team-map-filters">
        <Input
          placeholder="Filter technicians…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="h-8 w-48 text-sm"
          data-testid="team-map-filter-name"
        />
        {["all", "live", "stale", "offline", "unavailable"].map((k) => (
          <button
            key={k}
            onClick={() => setFilterStatus(k)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              filterStatus === k
                ? "bg-primary/15 text-primary border-primary/30"
                : "bg-card text-muted-foreground border-border hover:bg-muted"
            }`}
            data-testid={`team-map-filter-${k}`}
          >
            {k === "all" ? "All" : STATUS_META[k].label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        {/* Tech list */}
        <div className="rounded-2xl border border-border/60 bg-card overflow-y-auto max-h-[60vh] lg:max-h-[70vh] divide-y divide-border/40" data-testid="team-map-tech-list">
          {techs === null ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : visibleTechs.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4" data-testid="team-map-empty">No technicians match</p>
          ) : visibleTechs.map((t) => (
            <button
              key={t.technician_id}
              onClick={() => setSelectedId(t.technician_id)}
              className={`w-full text-left px-3 py-2.5 hover:bg-muted/40 ${selected?.technician_id === t.technician_id ? "bg-muted/50" : ""}`}
              data-testid={`team-map-tech-${t.technician_id}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: meta(t).color }} />
                <span className="text-sm font-semibold text-foreground truncate">{t.name}</span>
                <Badge className="text-[10px] px-1.5 border ml-auto" style={{ background: `${meta(t).color}15`, color: meta(t).color, borderColor: `${meta(t).color}30` }}>
                  {meta(t).label}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 pl-4">
                {t.location ? `Updated ${timeAgo(t.location.updated_at)}` : t.sharing_enabled ? "No location yet" : "Location sharing off"}
                {" · "}{t.jobs_today} job{t.jobs_today !== 1 ? "s" : ""} today
              </p>
            </button>
          ))}
        </div>

        {/* Map + detail */}
        <div className="space-y-3">
          <div className="relative rounded-2xl overflow-hidden border border-border/60" style={{ height: "58vh", minHeight: 360 }}>
            <div ref={mapRef} className="absolute inset-0" data-testid="team-map-canvas" />
            {mapError && (
              <div className="absolute inset-0 flex items-center justify-center bg-background z-10 p-6 text-center">
                <p className="text-sm text-muted-foreground max-w-sm" data-testid="team-map-error">{mapError}</p>
              </div>
            )}
            {routeData && (
              <div className="absolute top-2 left-2 z-20 flex items-center gap-2 rounded-xl bg-card border border-border px-3 py-1.5 shadow" data-testid="team-map-route-banner">
                <RouteIcon className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs text-foreground font-medium">{routeData.tech?.name}'s route</span>
                {routeData.route && (
                  <span className="text-[11px] text-muted-foreground">
                    {formatDistance(routeData.route.total_distance_m)} · {formatDuration(routeData.route.total_duration_s)}
                  </span>
                )}
                {routeData.failed && <span className="text-[11px] text-amber-500">failed to load</span>}
                <button onClick={clearRoute} className="text-muted-foreground" data-testid="team-map-route-clear"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>

          {/* Selected tech panel */}
          {selected && (
            <div className="rounded-2xl border border-border/60 bg-card p-4" data-testid="team-map-tech-detail">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta(selected).color }} />
                  <span className="text-base font-bold text-foreground">{selected.name}</span>
                  <Badge className="text-[10px] px-1.5 border" style={{ background: `${meta(selected).color}15`, color: meta(selected).color, borderColor: `${meta(selected).color}30` }}>
                    {meta(selected).label}
                  </Badge>
                  {selected.location && (
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Updated {timeAgo(selected.location.updated_at)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="h-8 text-xs" onClick={() => showRoute(selected)} disabled={routeLoading} data-testid="team-map-show-route">
                    {routeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RouteIcon className="w-3.5 h-3.5 mr-1" /> View day route</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedId(null)}>Close</Button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div className="rounded-xl bg-muted/40 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Current job</p>
                  {selected.current_job ? (
                    <>
                      <p className="text-sm font-medium text-foreground">{selected.current_job.title}</p>
                      <p className="text-xs text-muted-foreground">{selected.current_job.customer_name} · {selected.current_job.scheduled_time || "no time"} · {selected.current_job.status_label}</p>
                    </>
                  ) : <p className="text-xs text-muted-foreground">None in progress</p>}
                </div>
                <div className="rounded-xl bg-muted/40 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Next job</p>
                  {selected.next_job ? (
                    <>
                      <p className="text-sm font-medium text-foreground">{selected.next_job.title}</p>
                      <p className="text-xs text-muted-foreground">{selected.next_job.customer_name} · {selected.next_job.scheduled_time || "no time"}</p>
                    </>
                  ) : <p className="text-xs text-muted-foreground">Nothing scheduled next today</p>}
                </div>
              </div>
            </div>
          )}

          {/* Route stop list */}
          {routeData && (routeData.stops || []).length > 0 && (
            <div className="rounded-2xl border border-border/60 bg-card divide-y divide-border/40" data-testid="team-map-route-stops">
              {routeData.stops.map((s, i) => (
                <React.Fragment key={s.job_id}>
                  {i > 0 && routeData.route?.legs?.[i - 1] && (
                    <div className="px-4 py-1 text-[11px] text-muted-foreground bg-muted/30 flex items-center gap-2">
                      <Navigation className="w-3 h-3" />
                      {formatDistance(routeData.route.legs[i - 1].distance_m)} · {formatDuration(routeData.route.legs[i - 1].duration_s)}
                    </div>
                  )}
                  <div className="px-4 py-2 flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full text-[11px] font-bold text-white flex items-center justify-center flex-shrink-0" style={{ background: s.status_color || "#2563eb" }}>{s.stop_number}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{s.customer_name} — {s.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.scheduled_time || "No time"} · {s.customer_address} · {s.status_label}</p>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
